import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Sparkles, ThumbsUp, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BlueprintRating = ({ 
    blueprintId, 
    usedClarification = false,
    clarificationSkipped = false,
    promptId = null,
    onImprove 
}) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [rating, setRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleRatingClick = async (value) => {
        setRating(value);
        
        // Auto-submit rating
        setIsSubmitting(true);
        try {
            const headers = isAuthenticated ? getAuthHeaders() : {};
            await axios.post(
                `${BACKEND_URL}/api/blueprints/rate`,
                {
                    blueprint_id: blueprintId,
                    rating: value,
                    used_clarification: usedClarification,
                    clarification_skipped: clarificationSkipped,
                    prompt_id: promptId,
                    session_id: localStorage.getItem('vcc_session_id')
                },
                { headers }
            );
            
            setIsSubmitted(true);
            
            // Show improvement option for low ratings
            if (value <= 3) {
                setShowFeedback(true);
            }
            
        } catch (error) {
            console.error('Rating failed:', error);
            toast.error('Failed to submit rating');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFeedbackSubmit = async () => {
        if (!feedback.trim()) return;
        
        try {
            const headers = isAuthenticated ? getAuthHeaders() : {};
            await axios.post(
                `${BACKEND_URL}/api/blueprints/rate`,
                {
                    blueprint_id: blueprintId,
                    rating: rating,
                    used_clarification: usedClarification,
                    clarification_skipped: clarificationSkipped,
                    prompt_id: promptId,
                    feedback: feedback,
                    session_id: localStorage.getItem('vcc_session_id')
                },
                { headers }
            );
            
            setShowFeedback(false);
            toast.success('Feedback submitted. Thanks!');
            
        } catch (error) {
            console.error('Feedback failed:', error);
        }
    };

    if (isSubmitted && !showFeedback) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 text-sm text-zinc-400"
            >
                <ThumbsUp className="w-4 h-4 text-green-400" />
                <span>Thanks for rating!</span>
                {rating <= 3 && onImprove && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onImprove}
                        className="text-violet-400 hover:text-violet-300 ml-2"
                    >
                        <Sparkles className="w-3 h-3 mr-1" />
                        Improve it
                    </Button>
                )}
            </motion.div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-400">Rate this blueprint:</span>
                <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((value) => (
                        <button
                            key={value}
                            onClick={() => handleRatingClick(value)}
                            onMouseEnter={() => setHoveredRating(value)}
                            onMouseLeave={() => setHoveredRating(0)}
                            disabled={isSubmitting}
                            className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
                            data-testid={`rating-star-${value}`}
                        >
                            <Star
                                className={`w-5 h-5 transition-colors ${
                                    value <= (hoveredRating || rating)
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-zinc-600'
                                }`}
                            />
                        </button>
                    ))}
                </div>
            </div>

            {/* Feedback section for low ratings */}
            <AnimatePresence>
                {showFeedback && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-2"
                    >
                        <div className="flex items-center gap-2 text-sm text-zinc-400">
                            <MessageSquare className="w-4 h-4" />
                            <span>What could be better?</span>
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="Quick feedback..."
                                className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-violet-500"
                            />
                            <Button
                                size="sm"
                                onClick={handleFeedbackSubmit}
                                disabled={!feedback.trim()}
                                className="bg-violet-600 hover:bg-violet-500"
                            >
                                Send
                            </Button>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowFeedback(false)}
                                className="text-xs text-zinc-500 hover:text-zinc-400"
                            >
                                Skip
                            </button>
                            {onImprove && (
                                <button
                                    onClick={onImprove}
                                    className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1"
                                >
                                    <Sparkles className="w-3 h-3" />
                                    Regenerate with AI
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
